'use client';

import { c as _c } from "react/compiler-runtime";
import { decodeColor, formatCents } from 'betterplace-utils';
import { AnimateNumber } from 'design-system/client';
import { pick } from 'lodash';
import { useTranslations, useLocale } from 'next-intl';
import { type CSSProperties } from 'react';
import { composeSelectedFontObject, getClusterStyles } from '../helpers';
import StreambotWrapper from '../StreambotWrapper';
import type { StreambotComponentProps } from '../types';
import WidgetHeadline from '../WidgetHeadline';
import getDefaultValues from './getDefaultValues';
import type { TotalWidgetSettings } from './totalWidgetSettingsSchema';
import { useApiV4WithDemoData } from '@/api';
import useGoogleFonts from '@/fonts/useGoogleFonts';
const defaultDemoData = {
  donated_amount_in_cents: 65462
};
type TotalComponentData = {
  donated_amount_in_cents: number;
};
export type TotalProps = StreambotComponentProps<TotalWidgetSettings, TotalComponentData> & {
  subtractCents?: number;
};
function useData(fundraisingEventId, demoData) {
  const $ = _c(3);
  const locale = useLocale();
  let t0;
  if ($[0] !== locale) {
    t0 = {
      locale
    };
    $[0] = locale;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      selector(data) {
        if (!data) {
          return null;
        }
        return pick(data, ["donated_amount_in_cents"]);
      },
      intervalMs: 5000
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return useApiV4WithDemoData(`/fundraising_events/${fundraisingEventId}`, t0, t1, demoData);
}
function Total(t0) {
  const $ = _c(12);
  const {
    demoData: t1,
    withDemoData,
    fundraisingEventId,
    subtractCents,
    ...props
  } = t0;
  const demoData = t1 === undefined ? defaultDemoData : t1;
  const mergedProps = getDefaultValues(props);
  const {
    headlineText,
    headlineFont,
    headlineFontWeight,
    headlineFontSize,
    headlineFontColor,
    dataFont,
    dataFontWeight,
    dataFontSize,
    dataFontColor,
    countOfLines,
    textAlign,
    ...wrapperProps
  } = mergedProps;
  const fonts = [composeSelectedFontObject(headlineFont, headlineFontWeight), composeSelectedFontObject(dataFont, dataFontWeight)];
  useGoogleFonts({
    fonts
  });
  const locale = useLocale();
  const {
    data
  } = useData(fundraisingEventId, withDemoData ? demoData : undefined);
  const t = useTranslations("nextjs.streambot");
  if (!data) {
    return null;
  }
  const {
    donated_amount_in_cents: donatedAmountInCents
  } = data;
  const total = donatedAmountInCents - (subtractCents ?? 0);
  const clusterAlign = countOfLines === "single";
  const t2 = getClusterStyles(clusterAlign, textAlign);
  const t3 = "totalData";
  const t4 = `${dataFontSize}px`;
  const t5 = decodeColor(dataFontColor?.toString());
  let t6;
  if ($[0] !== dataFont || $[1] !== dataFontWeight || $[2] !== t4 || $[3] !== t5) {
    t6 = {
      "--streambot-total-data-font": dataFont,
      "--streambot-total-data-font-weight": dataFontWeight,
      "--streambot-total-data-font-size": t4,
      "--streambot-total-data-font-color": t5
    };
    $[0] = dataFont;
    $[1] = dataFontWeight;
    $[2] = t4;
    $[3] = t5;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const t7 = t6 as CSSProperties;
  let t8;
  if ($[5] !== locale || $[6] !== t || $[7] !== total) {
    t8 = total <= 0 ? t("placeholder") : <AnimateNumber animationDurationInMs={1500} maxNumberScale={1.3}>{formatCents(locale, total)}</AnimateNumber>;
    $[5] = locale;
    $[6] = t;
    $[7] = total;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== t7 || $[10] !== t8) {
    t9 = <div className={t3} style={t7}>{t8}</div>;
    $[9] = t7;
    $[10] = t8;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  return <StreambotWrapper textAlign={textAlign} {...wrapperProps}><div className={t2}><WidgetHeadline content={headlineText ? headlineText : t("total_headline")} headlineFont={headlineFont} headlineFontWeight={headlineFontWeight} headlineFontSize={headlineFontSize} headlineFontColor={headlineFontColor} />{t9}</div></StreambotWrapper>;
}
export default Total;